/**
 * Integrytas Group - Application State & Session Fingerprinting (ASF)
 * asf - builds the client context envelope sent with every gateway request as
 * the `x-asf-context` header. The server (asf_capture) enriches it with IP,
 * user, millisecond timestamp, and the live role/permission matrix.
 *
 *   setAsfContext({ screen_code, tab_id })   // call on screen/tab change
 *   markAsfSessionStart()                    // call once on login
 */

interface AsfContext { screen_code?: string; tab_id?: string }

let ctx: AsfContext = {};
let fingerprintHash = '';
let sessionStartedAt = '';

export function setAsfContext(patch: AsfContext): void { ctx = { ...ctx, ...patch }; }
export function markAsfSessionStart(): void { if (!sessionStartedAt) sessionStartedAt = new Date().toISOString(); }

// Stable, non-cryptographic composite hash of coarse device signals (FNV-1a).
function fnv(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(16).padStart(8, '0');
}

function fingerprint(): string {
  if (fingerprintHash) return fingerprintHash;
  try {
    const parts = [
      navigator.userAgent,
      `${screen.width}x${screen.height}x${screen.colorDepth}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.language,
      String(navigator.hardwareConcurrency ?? ''),
    ].join('|');
    fingerprintHash = `fp_${fnv(parts)}`;
  } catch { fingerprintHash = 'fp_unknown'; }
  return fingerprintHash;
}

/** Active workspace code (shared with the UNE/GTC workspace context). */
export function asfWorkspaceCode(): string {
  try { return localStorage.getItem('une.active_workspace') ?? ''; } catch { return ''; }
}

/** The x-asf-context header value. Cheap + synchronous (no network). */
export function asfContextHeader(): string {
  return JSON.stringify({
    workspace_code: asfWorkspaceCode() || undefined,
    screen_code: ctx.screen_code,
    tab_id: ctx.tab_id,
    fingerprint_hash: fingerprint(),
    session_started_at: sessionStartedAt || undefined,
  });
}
